import React, { useMemo, useState, useEffect } from 'react';
import { products } from '../../utils/productsData';
import { servicesCardData as defaultServicesData } from '../../utils/servicesCardData';
import '../../styles/AdminPages.css';

// Fallback if import fails
const getDefaultServicesData = () => {
  try {
    return defaultServicesData || {};
  } catch (error) {
    console.error('Error loading default services data:', error);
    return {};
  }
};

const Dashboard = () => {
  const [servicesData, setServicesData] = useState({});

  // Load merged services data (default + admin additions, excluding deleted)
  useEffect(() => {
    const getMergedServicesData = () => {
      const savedServices = localStorage.getItem('adminServicesData');
      const deletedArtists = JSON.parse(localStorage.getItem('deletedArtists') || '{}');
      let mergedData = {};
      
      const defaultData = getDefaultServicesData();
      
      // Start with default data - deep copy and filter out deleted artists
      Object.keys(defaultData).forEach(category => {
        const deletedIds = new Set(deletedArtists[category] || []);
        mergedData[category] = (defaultData[category] || []).filter(
          artist => !deletedIds.has(artist.id)
        );
      });
      
      // Merge admin-added artists
      if (savedServices) {
        try {
          const parsed = JSON.parse(savedServices);
          Object.keys(parsed).forEach(category => {
            const deletedIds = new Set(deletedArtists[category] || []);
            if (mergedData[category]) {
              const existingIds = new Set(mergedData[category].map(a => a.id));
              const adminArtists = (parsed[category] || []).filter(
                a => !existingIds.has(a.id) && !deletedIds.has(a.id)
              );
              mergedData[category] = [...mergedData[category], ...adminArtists];
            } else {
              mergedData[category] = (parsed[category] || []).filter(
                a => !deletedIds.has(a.id)
              );
            }
          });
        } catch (error) {
          console.error('Error loading services data:', error);
        }
      }
      
      return mergedData;
    };

    setServicesData(getMergedServicesData());

    // Listen for updates
    const handleStorageChange = () => {
      setServicesData(getMergedServicesData());
    };
    window.addEventListener('adminServicesUpdated', handleStorageChange);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('adminServicesUpdated', handleStorageChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Calculate statistics from products data
  const productStats = useMemo(() => {
    // Filter out header items (they're not actual products)
    const actualProducts = products.filter(p => p.type !== 'header' && p.id);
    
    // Get unique categories
    const categories = [...new Set(actualProducts.map(p => p.category))];
    
    // Get products by category count
    const categoryCounts = categories.map(cat => ({
      category: cat,
      count: actualProducts.filter(p => p.category === cat).length
    })).sort((a, b) => b.count - a.count);
    
    // Get recent products (last 5)
    const recentProducts = actualProducts
      .filter(p => p.type !== 'header')
      .slice(-5)
      .reverse();
    
    return {
      totalProducts: actualProducts.length,
      totalCategories: categories.length,
      categoryCounts,
      recentProducts
    };
  }, []);

  // Calculate statistics from services data
  const serviceStats = useMemo(() => {
    const categories = Object.keys(servicesData);
    let totalArtists = 0;
    const categoryCounts = [];
    const recentArtists = [];

    categories.forEach(category => {
      const artists = servicesData[category] || [];
      const count = artists.length;
      totalArtists += count;
      
      if (count > 0) {
        categoryCounts.push({
          category,
          count
        });

        // Get the most recent artist from this category (if any)
        if (artists.length > 0) {
          recentArtists.push(...artists.slice(-1).map(artist => ({
            ...artist,
            category
          })));
        }
      }
    });

    // Sort by count and get top categories
    categoryCounts.sort((a, b) => b.count - a.count);

    // Get last 5 artists across all categories
    const allArtists = [];
    categories.forEach(category => {
      const artists = servicesData[category] || [];
      artists.forEach(artist => {
        allArtists.push({ ...artist, category });
      });
    });
    const sortedRecentArtists = allArtists.slice(-5).reverse();

    return {
      totalCategories: categories.length,
      totalArtists,
      categoryCounts: categoryCounts.slice(0, 6),
      recentArtists: sortedRecentArtists
    };
  }, [servicesData]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1>Dashboard</h1>
        <p>Welcome to the BookMyCamera Admin Panel</p>
      </div>
      
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="admin-stat-icon" style={{ background: '#3b82f6' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7"></rect>
              <rect x="14" y="3" width="7" height="7"></rect>
              <rect x="14" y="14" width="7" height="7"></rect>
              <rect x="3" y="14" width="7" height="7"></rect>
            </svg>
          </div>
          <div className="admin-stat-content">
            <h3>Total Products</h3>
            <p className="admin-stat-number">{productStats.totalProducts}</p>
          </div>
        </div>
        
        <div className="admin-stat-card">
          <div className="admin-stat-icon" style={{ background: '#f59e0b' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
            </svg>
          </div>
          <div className="admin-stat-content">
            <h3>Service Categories</h3>
            <p className="admin-stat-number">{serviceStats.totalCategories}</p>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-icon" style={{ background: '#ec4899' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
          </div>
          <div className="admin-stat-content">
            <h3>Total Artists</h3>
            <p className="admin-stat-number">{serviceStats.totalArtists}</p>
          </div>
        </div>
        
        <div className="admin-stat-card">
          <div className="admin-stat-icon" style={{ background: '#10b981' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="9" cy="21" r="1"></circle>
              <circle cx="20" cy="21" r="1"></circle>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
            </svg>
          </div>
          <div className="admin-stat-content">
            <h3>Total Orders</h3>
            <p className="admin-stat-number">0</p>
            <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '0.25rem 0 0 0' }}>Coming soon</p>
          </div>
        </div>
        
        <div className="admin-stat-card">
          <div className="admin-stat-icon" style={{ background: '#8b5cf6' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              <polyline points="9 22 9 12 15 12 15 22"></polyline>
            </svg>
          </div>
          <div className="admin-stat-content">
            <h3>Product Categories</h3>
            <p className="admin-stat-number">{productStats.totalCategories}</p>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2rem' }}>
        {/* Products by Category */}
        <div className="admin-section">
          <h2>Products by Category</h2>
          <div className="admin-card">
            {productStats.categoryCounts.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {productStats.categoryCounts.slice(0, 6).map((item, index) => (
                  <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '0.75rem', borderBottom: index < productStats.categoryCounts.slice(0, 6).length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                    <span style={{ color: '#374151', fontWeight: '500' }}>{item.category}</span>
                    <span style={{ color: '#3b82f6', fontWeight: '700', fontSize: '1rem' }}>{item.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: '#6b7280', textAlign: 'center', padding: '1rem' }}>No categories found</p>
            )}
          </div>
        </div>

        {/* Service Categories */}
        <div className="admin-section">
          <h2>Services by Category</h2>
          <div className="admin-card">
            {serviceStats.categoryCounts.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {serviceStats.categoryCounts.map((item, index) => (
                  <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '0.75rem', borderBottom: index < serviceStats.categoryCounts.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                    <span style={{ color: '#374151', fontWeight: '500' }}>{item.category}</span>
                    <span style={{ color: '#f59e0b', fontWeight: '700', fontSize: '1rem' }}>{item.count} {item.count === 1 ? 'artist' : 'artists'}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: '#6b7280', textAlign: 'center', padding: '1rem' }}>No service categories found</p>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2rem' }}>
        {/* Recent Products */}
        <div className="admin-section">
          <h2>Recent Products</h2>
          <div className="admin-card">
            {productStats.recentProducts.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {productStats.recentProducts.map((product) => (
                  <div key={product.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '0.75rem', borderBottom: productStats.recentProducts.indexOf(product) < productStats.recentProducts.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, color: '#374151', fontWeight: '500', fontSize: '0.875rem' }}>{product.name}</p>
                      <p style={{ margin: '0.25rem 0 0 0', color: '#6b7280', fontSize: '0.75rem' }}>{product.category}</p>
                    </div>
                    <span style={{ color: '#10b981', fontWeight: '700', fontSize: '0.875rem' }}>{formatCurrency(product.price || 0)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: '#6b7280', textAlign: 'center', padding: '1rem' }}>No products found</p>
            )}
          </div>
        </div>

        {/* Recent Artists */}
        <div className="admin-section">
          <h2>Recent Artists</h2>
          <div className="admin-card">
            {serviceStats.recentArtists.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {serviceStats.recentArtists.map((artist) => (
                  <div key={artist.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '0.75rem', borderBottom: serviceStats.recentArtists.indexOf(artist) < serviceStats.recentArtists.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <img 
                        src={artist.image} 
                        alt={artist.name}
                        style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&auto=format&q=80';
                        }}
                      />
                      <div>
                        <p style={{ margin: 0, color: '#374151', fontWeight: '500', fontSize: '0.875rem' }}>{artist.name}</p>
                        <p style={{ margin: '0.25rem 0 0 0', color: '#6b7280', fontSize: '0.75rem' }}>{artist.category}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: '#6b7280', textAlign: 'center', padding: '1rem' }}>No artists found</p>
            )}
          </div>
        </div>
      </div>
      
      <div className="admin-section">
        <h2>Quick Actions</h2>
        <div className="admin-card">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <button className="admin-button primary" style={{ padding: '1rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              <span>Add Product</span>
            </button>
            <button className="admin-button secondary" style={{ padding: '1rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
              <span>View Orders</span>
            </button>
            <button className="admin-button secondary" style={{ padding: '1rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
              <span>Manage Users</span>
            </button>
            <button className="admin-button secondary" style={{ padding: '1rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M12 1v6m0 6v6M5.64 5.64l4.24 4.24m4.24 4.24l4.24 4.24M1 12h6m6 0h6M5.64 18.36l4.24-4.24m4.24-4.24l4.24-4.24"></path>
              </svg>
              <span>Settings</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

